import React, { useState, useEffect } from 'react';
import { DollarSign, Users, AlertCircle, TrendingUp, Loader2, Package, Briefcase } from 'lucide-react';
import { reportsService } from '../../services/reportsService';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const formatMoney = (value, currency) => {
  const num = Number(value || 0).toLocaleString();
  if (!currency) return num;
  const symbols = { PKR: 'PKR ', USD: '$ ', EUR: '€ ', GBP: '£ ', AED: 'AED ' };
  return (symbols[currency] || currency + ' ') + num;
};

const KpiCard = ({ label, value, icon, sublabel, onClick }) => (
  <div
    onClick={onClick}
    className={
      'bg-white rounded-card shadow-soft border border-gray-100 p-4' +
      (onClick ? ' cursor-pointer hover:shadow-elevated hover:border-purple-300 transition-all' : '')
    }
  >
    <div className="flex items-center justify-between mb-4">
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">{label}</p>
      {icon}
    </div>
    <p className="text-2xl font-bold text-purple-600">{value}</p>
    {sublabel && <p className="text-xs text-gray-400 mt-1">{sublabel}</p>}
    <div className="h-1 w-10 bg-purple-600 rounded-full mt-4"></div>
  </div>
);

const SuperAdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const dashRes = await reportsService.getExecutiveDashboard();
        console.log('Dashboard data:', dashRes.data);
        setSummary(dashRes.data);
      } catch (err) {
        setError(err.message || 'Failed to load dashboard');
        console.error('Dashboard error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const safeGet = (obj, path, defaultVal = 0) => {
    try {
      return path.split('.').reduce((acc, part) => acc?.[part], obj) ?? defaultVal;
    } catch {
      return defaultVal;
    }
  };

  const commissionByCurrency = safeGet(summary, 'commission_liability_by_currency', []);
  const commissionLiabilityValue = commissionByCurrency.length > 0
    ? commissionByCurrency.map((c) => formatMoney(c.total_liability, c.currency)).join('  +  ')
    : formatMoney(safeGet(summary, 'pending_commission_liability'));
  const commissionLiabilitySublabel = commissionByCurrency.length > 1
    ? 'Across ' + commissionByCurrency.length + ' currencies'
    : null;

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome Back Admin, {user ? user.full_name : ''}
        </h1>
        <p className="text-sm text-gray-500 mt-1">Company-wide overview & performance metrics</p>
      </div>

      {error && (
        <div className="mb-4 px-4 py-2.5 rounded-control bg-red-50 text-red-700 text-sm">
          {error}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-20 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      )}

      {!loading && summary && (
        <div className="space-y-4">
          {/* Top KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard
              label="Revenue Received"
              value={'$' + formatMoney(safeGet(summary, 'revenue.total_received'))}
              sublabel={safeGet(summary, 'revenue.collection_rate') + '% collected'}
              icon={<DollarSign className="w-4 h-4 text-purple-600" />}
              onClick={() => navigate('/contracts')}
            />
            <KpiCard
              label="Active Employees"
              value={safeGet(summary, 'headcount')}
              icon={<Users className="w-4 h-4 text-purple-600" />}
              onClick={() => navigate('/employees')}
            />
            <KpiCard
              label="Pending Expenses"
              value={safeGet(summary, 'pending_expenses_count')}
              icon={<AlertCircle className="w-4 h-4 text-purple-600" />}
              onClick={() => navigate('/expenses')}
            />
            <KpiCard
              label="Open Leads"
              value={safeGet(summary, 'leads.open')}
              sublabel={safeGet(summary, 'leads.won') + ' won'}
              icon={<TrendingUp className="w-4 h-4 text-purple-600" />}
              onClick={() => navigate('/sales')}
            />
          </div>

          {/* Receivables Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard
              label="Pending Receivables"
              value={'$' + formatMoney(safeGet(summary, 'revenue.total_pending'))}
              icon={<DollarSign className="w-4 h-4 text-purple-600" />}
              onClick={() => navigate('/contracts')}
            />
            <KpiCard
              label="Overdue Receivables"
              value={'$' + formatMoney(safeGet(summary, 'revenue.total_overdue'))}
              icon={<AlertCircle className="w-4 h-4 text-purple-600" />}
              onClick={() => navigate('/contracts')}
            />
            <KpiCard
              label="Commission Liability"
              value={commissionLiabilityValue}
              sublabel={commissionLiabilitySublabel}
              icon={<DollarSign className="w-4 h-4 text-purple-600" />}
              onClick={() => navigate('/commissions')}
            />
            <KpiCard
              label="Total Assets"
              value={safeGet(summary, 'total_assets')}
              icon={<Package className="w-4 h-4 text-purple-600" />}
              onClick={() => navigate('/assets')}
            />
          </div>

          {/* Payroll Alert */}
          {safeGet(summary, 'pending_payroll_batches') > 0 && (
            <div className="bg-purple-50 rounded-card shadow-soft border border-purple-200 p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-purple-600 shrink-0" />
              <p className="text-sm text-gray-700 flex-1">
                <span className="font-semibold text-purple-900">{safeGet(summary, 'pending_payroll_batches')} payroll batch(es)</span> awaiting review or approval.
              </p>
            </div>
          )}

          {/* Beautiful Design Card */}
          <div className="mt-6 bg-gradient-to-r from-purple-600 to-purple-700 rounded-card shadow-soft border border-purple-400 p-8 text-white overflow-hidden relative">
            <div className="absolute top-0 right-0 w-40 h-40 bg-purple-500/20 rounded-full -mr-20 -mt-20"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-500/20 rounded-full -ml-16 -mb-16"></div>
            
            <div className="relative z-10">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <p className="text-sm font-medium opacity-90 mb-2">Financial Health</p>
                  <h3 className="text-3xl font-bold">Dashboard</h3>
                </div>
                <Briefcase className="w-10 h-10 opacity-60" />
              </div>

              <p className="text-sm opacity-85 max-w-md mb-6">
                Real-time financial overview of your organization. Track revenue, expenses, and receivables with precision. All metrics are automatically updated from your contracts, payroll, and financial records.
              </p>

              <div className="grid grid-cols-3 gap-4 pt-6 border-t border-purple-400/50">
                <div>
                  <p className="text-xs opacity-75 mb-1">Total Contract Value</p>
                  <p className="text-lg font-semibold">${formatMoney(safeGet(summary, 'revenue.total_contract_value'))}</p>
                </div>
                <div>
                  <p className="text-xs opacity-75 mb-1">Outstanding Amount</p>
                  <p className="text-lg font-semibold">${formatMoney(safeGet(summary, 'revenue.total_pending') + safeGet(summary, 'revenue.total_overdue'))}</p>
                </div>
                <div>
                  <p className="text-xs opacity-75 mb-1">Team Size</p>
                  <p className="text-lg font-semibold">{safeGet(summary, 'headcount')} employees</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;